import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { fetchSkuQrSticker, SKU_QR_SIZE_OPTIONS, SkuBoundMaterial, SkuQrSize } from '../../api/material';
import { ApiError, saveBlob } from '../../api/request';
import { I18n, i18nText } from '../../utils/i18n';
import SearchSelect from '../../components/SearchSelect';

// SKU 二维码贴纸弹窗 (2026-09-07): 选尺寸 + 自助编辑二维码名称 → 生成预览 → 下载 PNG.
// 贴纸正文格式 = "<材质编码>: <二维码名称>", 名称默认主绑定材料英文名 (与 CMS 贴纸一致), 可一键换成 SKU 商品名或手改;
// 二维码内容 = https://s.xtool.com/mt/<所选材料的材料ID(legacy_mid)>, 与主站 CMS 同一套码 (未绑定材料的 SKU 生成不了).
// SKU↔材料多对多 (2026-09-08): 绑定多个材料时弹窗内下拉选按哪条材质编码出码, 默认主绑定; 材料详情入口固定为本页材料.
// 预览与下载共用同一份 PNG 字节 (fetch 一次), 改了名称/尺寸/材料后预览标记过期, 需重新生成才能下载.

export interface SkuQrTarget {
    skuCode: string;
    productName?: I18n;
    /** 可选的绑定材料 (主绑定在前); 空数组 = 未绑定 (无法生成). 只有一条时不显示下拉. */
    materials: SkuBoundMaterial[];
}

/** 按英文优先取名 (贴纸字体只覆盖拉丁字符, 英文名是主用途). */
const enFirst = (n?: I18n): string => (n?.en || '').trim() || i18nText(n || {}).trim();

/** 文件名安全化: 去掉路径/保留字符. */
const safeFileName = (s: string) => s.replace(/[\\/:*?"<>|\r\n]+/g, '_').replace(/\s+/g, ' ').trim();

function SkuQrModal({ open, target, onClose }: { open: boolean; target: SkuQrTarget | null; onClose: () => void }) {
    const [size, setSize] = useState<SkuQrSize>('dq003');
    const [name, setName] = useState('');
    // 按哪条绑定材料出码 (材质编码); 未绑定时为 ''
    const [xcode, setXcode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState<{
        blob: Blob;
        url: string;
        size: SkuQrSize;
        name: string;
        xcode: string;
    } | null>(null);

    const materials = target?.materials || [];
    const material = materials.find((m) => m.xcode === xcode);

    // 打开时按目标 SKU 初始化: 材料默认主绑定, 名称默认该材料英文名 (无材料时退回 SKU 商品名)
    useEffect(() => {
        if (!open || !target) return;
        const primary = target.materials.find((m) => m.isPrimary) || target.materials[0];
        setSize('dq003');
        setXcode(primary?.xcode || '');
        setName(enFirst(primary?.name) || enFirst(target.productName));
        setError('');
        setPreview(null);
    }, [open, target]);

    // object URL 用完回收 (换预览 / 关弹窗)
    useEffect(() => {
        if (!preview) return;
        return () => URL.revokeObjectURL(preview.url);
    }, [preview]);

    const sizeOpt = useMemo(() => SKU_QR_SIZE_OPTIONS.find((o) => o.key === size) || SKU_QR_SIZE_OPTIONS[0], [size]);
    const trimmedName = name.trim();
    const unbound = materials.length === 0;
    const canGenerate = !!target && xcode !== '' && trimmedName !== '' && !busy;
    // 预览是否与当前参数一致; 不一致时不允许直接下载 (避免下到旧图)
    const previewFresh =
        !!preview && preview.size === size && preview.name === trimmedName && preview.xcode === xcode;

    const onGenerate = async () => {
        if (!target || !canGenerate) return;
        setBusy(true);
        setError('');
        try {
            const blob = await fetchSkuQrSticker({ skuCode: target.skuCode, size, name: trimmedName, xcode });
            setPreview({ blob, url: URL.createObjectURL(blob), size, name: trimmedName, xcode });
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setBusy(false);
        }
    };

    const onDownload = () => {
        if (!target || !preview || !previewFresh) return;
        const base = safeFileName(`${preview.xcode} ${preview.name}`) || target.skuCode;
        saveBlob(preview.blob, `${base}_${preview.size}.png`);
    };

    if (!target) return null;
    const materialNameText = enFirst(material?.name);
    const productNameText = enFirst(target.productName);

    return (
        <Modal open={open} width={1080} title="生成二维码" onClose={onClose}>
            <div className="grid grid-cols-[400px_minmax(0,1fr)] gap-8">
                {/* 左: SKU 信息 / 名称编辑 / 尺寸 */}
                <div className="space-y-5 min-w-0">
                    <div className="rounded-lg bg-slate-50 p-4 space-y-1">
                        <div className="font-mono font-semibold text-slate-800">{target.skuCode}</div>
                        <div className="text-sm text-slate-700">{i18nText(target.productName || {}) || '—'}</div>
                        {target.productName?.en && (
                            <div className="text-xs text-slate-500">{target.productName.en}</div>
                        )}
                    </div>

                    {unbound ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                            该 SKU 未绑定材料, 二维码内容是绑定材料的材料ID, 无法生成。请先在「编辑」里绑定材料。
                        </div>
                    ) : (
                        <div>
                            <div className="font-semibold text-sm mb-2">
                                材质编码
                                {materials.length > 1 && (
                                    <span className="ml-2 text-xs font-normal text-slate-400">
                                        该 SKU 绑定了 {materials.length} 个材料, 选一个出码
                                    </span>
                                )}
                            </div>
                            {materials.length > 1 ? (
                                <SearchSelect
                                    className="w-full font-mono"
                                    value={xcode}
                                    onChange={(v) => setXcode(v)}
                                    options={materials.map((m) => ({
                                        value: m.xcode,
                                        label: `${m.isPrimary ? '★ ' : ''}${m.xcode}${enFirst(m.name) ? ` · ${i18nText(m.name || {})}` : ''}`,
                                    }))}
                                />
                            ) : (
                                <div className="text-sm">
                                    <span className="font-mono text-slate-700">{xcode}</span>
                                    {material?.name && (
                                        <span className="ml-2 text-slate-500">{i18nText(material.name)}</span>
                                    )}
                                </div>
                            )}
                            <div className="mt-1 text-[11px] text-slate-400">
                                贴纸粗体前缀 = 材质编码; 码内容 = 该材料的材料ID{materials.length > 1 ? ' (★ 为主绑定)' : ''}
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="font-semibold text-sm mb-2">二维码名称</div>
                        <input
                            className="inp w-full"
                            value={name}
                            maxLength={120}
                            placeholder="贴纸上的名称 (建议英文)"
                            onChange={(e) => setName(e.target.value)}
                        />
                        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="text-slate-400">快捷填充:</span>
                            <button
                                type="button"
                                className="text-brand-600 hover:underline disabled:text-slate-300 disabled:no-underline"
                                disabled={!materialNameText}
                                title={materialNameText ? undefined : '所选材料没有名称'}
                                onClick={() => setName(materialNameText)}
                            >
                                材料名
                            </button>
                            <button
                                type="button"
                                className="text-brand-600 hover:underline disabled:text-slate-300 disabled:no-underline"
                                disabled={!productNameText}
                                onClick={() => setName(productNameText)}
                            >
                                SKU 商品名
                            </button>
                        </div>
                        <div className="mt-2 text-[11px] text-slate-500">
                            贴纸正文:{' '}
                            <span className="font-mono text-slate-700">
                                {xcode ? <b>{xcode}: </b> : <b className="text-slate-400">{'{材质编码}'}: </b>}
                                {trimmedName || <span className="text-slate-300">(空)</span>}
                            </span>
                        </div>
                    </div>

                    <div>
                        <div className="font-semibold text-sm mb-2">选择二维码尺寸</div>
                        <div className="space-y-2">
                            {SKU_QR_SIZE_OPTIONS.map((o) => {
                                const active = o.key === size;
                                return (
                                    <label
                                        key={o.key}
                                        className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${
                                            active ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:border-slate-300'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="sku-qr-size"
                                            className="mt-1"
                                            checked={active}
                                            onChange={() => setSize(o.key)}
                                        />
                                        <div className="text-sm">
                                            <div className="flex items-center gap-2 font-medium">
                                                {o.label}
                                                <span className="tag bg-brand-50 text-brand-700 font-mono text-[11px]">
                                                    {o.width} x {o.height}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5">{o.desc}</div>
                                            <div className="text-xs text-slate-500">
                                                输出 {o.width} x {o.height} px PNG (300 DPI)
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 右: 预览 (棋盘格底) */}
                <div
                    className="relative flex min-h-[480px] items-center justify-center rounded-lg border border-dashed border-slate-300 p-4"
                    style={{
                        backgroundColor: '#f5f7fb',
                        backgroundImage:
                            'linear-gradient(45deg, #e5e9f2 25%, transparent 25%), linear-gradient(-45deg, #e5e9f2 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e9f2 75%), linear-gradient(-45deg, transparent 75%, #e5e9f2 75%)',
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0',
                    }}
                >
                    {preview ? (
                        <>
                            <img
                                src={preview.url}
                                alt="二维码贴纸预览"
                                className="max-h-[560px] max-w-full shadow"
                                style={{ aspectRatio: `${sizeOpt.width} / ${sizeOpt.height}` }}
                            />
                            {!previewFresh && (
                                <div className="absolute inset-x-4 bottom-4 rounded bg-amber-50/95 border border-amber-200 px-3 py-1.5 text-center text-xs text-amber-800">
                                    名称、尺寸或材质编码已变更, 请重新「生成预览」后再下载
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center text-sm text-slate-400">
                            <div className="text-4xl mb-3">▦</div>
                            <div>选择尺寸后点击「生成预览」</div>
                            <div>确认无误后可下载图片</div>
                        </div>
                    )}
                </div>
            </div>

            {error && <div className="mt-4 text-sm text-rose-600">{error}</div>}

            <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
                    取 消
                </button>
                <button type="button" className="btn-primary" onClick={onGenerate} disabled={!canGenerate}>
                    {busy ? '生成中…' : '生成预览'}
                </button>
                <button type="button" className="btn-ghost" onClick={onDownload} disabled={!previewFresh || busy}>
                    下载图片
                </button>
            </div>
        </Modal>
    );
}

export default SkuQrModal;
